import { useRef, useState } from 'react';

export interface IUseActionSubmitOptions<TResult, TError, TArgs extends unknown[]> {
    /** Закрытие модалки действия: успех либо отказ */
    completeHandler?: (isSucceed: boolean) => void;
    /** Запрос действия; аргументы `okClick` приходят сюда (у формы это её значения) */
    submit: (...args: TArgs) => Promise<TResult>;
    /** Что считать успехом; по умолчанию — `completeHandler(true)` */
    onSuccess?: (result: TResult) => void;
    /**
     * Во что превратить отказ; по умолчанию — как есть. Тип отказа по умолчанию `string`:
     * так было в модалках до хука (`useState<string>()` плюс `setErrorText(error)` с `any`),
     * а `MessageDialog` и так разбирает всё, чем отклонил интерцептор.
     */
    mapError?: (error: unknown) => TError;
    /** Гасить прошлый отказ перед запросом — так делает часть модалок */
    resetErrorOnSubmit?: boolean;
    /** Стартовое busy-состояние: модалки, которые сперва грузят модель, начинают с `true` */
    initialBusy?: boolean;
}

/**
 * Общая обвязка сабмита модалки действия: состояние отказа, busy на время запроса
 * и закрытие по «Отмене». Показывать `isBusy` (оверлей загрузки) решает сама модалка — хук
 * только считает состояние. `isBusy` покрывает и подготовку (модалки с `initialBusy`), а
 * `isSubmitting` — только сам запрос действия: на это время закрытие окна блокируется
 * (`closeDisabled` модалки), а `cancelClick` ничего не делает — крестик и Escape окна
 * без `closeDisabled` не оборвут запрос, результат которого уже не будет показан.
 */
export const useActionSubmit = <TResult = unknown, TError = string, TArgs extends unknown[] = []>({
    completeHandler,
    submit,
    onSuccess,
    mapError,
    resetErrorOnSubmit = false,
    initialBusy,
}: IUseActionSubmitOptions<TResult, TError, TArgs>) => {
    const [errorText, setErrorText] = useState<TError | undefined>(undefined);
    const [isBusy, setIsBusy] = useState<boolean | undefined>(initialBusy);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const okClick = (...args: TArgs) => {
        if (resetErrorOnSubmit) setErrorText(undefined);
        setIsBusy(true);
        submittingRef.current = true;
        setIsSubmitting(true);

        // Синхронный отказ сборки запроса — тот же путь, что и отказ сервера: иначе окно
        // осталось бы запертым без finally. Исполнитель промиса вызывает submit сразу.
        const request = new Promise<TResult>((resolve) => resolve(submit(...args)));

        return request
            .then((result) => {
                if (onSuccess) onSuccess(result);
                else if (completeHandler) completeHandler(true);
            })
            .catch((error: unknown) => {
                setErrorText((mapError ? mapError(error) : error) as TError);
            })
            .finally(() => {
                submittingRef.current = false;
                setIsSubmitting(false);
                setIsBusy(false);
            });
    };

    const cancelClick = () => {
        if (submittingRef.current) return;
        if (completeHandler) completeHandler(false);
    };

    return { errorText, setErrorText, isBusy, setIsBusy, isSubmitting, okClick, cancelClick };
};
